import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, XCircle, RefreshCw, Plug, Loader2, Euro, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { apiPaymentsDiagnostic, apiVerifyPayments, apiSumupOrders, apiSumupSummary, apiSumupRecheck, eur, errMsg } from "../adminApi";

const PS = {
  paid: { label: "Pagato", cls: "bg-lime/15 text-lime border border-lime/40" },
  pending: { label: "In attesa", cls: "bg-surface3 text-steel border border-line" },
  failed: { label: "Fallito", cls: "bg-destructive/15 text-destructive border border-destructive/40" },
  expired: { label: "Scaduto", cls: "bg-carrot/15 text-carrot border border-carrot/40" },
  mismatch: { label: "Discrepanza", cls: "bg-carrot/15 text-carrot border border-carrot/40" },
  refunded: { label: "Rimborsato", cls: "bg-surface3 text-steel border border-line" },
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function StatusPill({ s }) {
  const cfg = PS[s] || PS.pending;
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${cfg.cls}`}>{cfg.label}</span>;
}

export default function Payments() {
  const [diag, setDiag] = useState(null);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyRes, setVerifyRes] = useState(null);
  const [recheckingId, setRecheckingId] = useState(null);
  const timerRef = useRef(null);

  const loadDiag = () => apiPaymentsDiagnostic().then(setDiag).catch((e) => toast.error(errMsg(e)));
  const loadLive = async () => {
    try {
      const [s, o] = await Promise.all([apiSumupSummary(), apiSumupOrders(50)]);
      setSummary(s);
      setOrders(o.items || []);
    } catch (e) { /* silenzioso durante il polling */ }
  };

  useEffect(() => {
    loadDiag();
    loadLive();
    // Polling ogni 10s: gli stati si aggiornano da soli senza ricaricare la pagina.
    timerRef.current = setInterval(loadLive, 10000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, []);

  const runVerify = async () => {
    setVerifying(true);
    setVerifyRes(null);
    try {
      const res = await apiVerifyPayments();
      setVerifyRes(res);
      if (res.ok) toast.success(`Connessione SumUp OK · ${res.merchant_name || "merchant"}`);
      else toast.error(res.detail || "Verifica non riuscita");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setVerifying(false); }
  };

  const runRecheck = async (orderNumber) => {
    setRecheckingId(orderNumber);
    try {
      await apiSumupRecheck(orderNumber);
      await loadLive();
      toast.success(`Ordine ${orderNumber} ricontrollato`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRecheckingId(null); }
  };

  const configured = diag?.configured;

  return (
    <div className="flex flex-col gap-5" data-testid="payments-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Pagamenti · SumUp</h1>
        <p className="mt-1 text-sm text-steel">Monitoraggio in tempo reale di checkout e incassi.</p>
      </div>

      {/* Contatori in alto */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card className="border-line bg-surface2"><CardContent className="flex items-center gap-3 p-4">
          <CheckCircle2 className="h-5 w-5 text-lime" />
          <div><div className="text-xs text-steel">Ordini pagati oggi</div>
            <div className="font-display text-xl font-semibold text-chalk" data-testid="sumup-paid-today">{summary ? summary.paid_today : "—"}</div></div>
        </CardContent></Card>
        <Card className="border-line bg-surface2"><CardContent className="flex items-center gap-3 p-4">
          <Euro className="h-5 w-5 text-lime" />
          <div><div className="text-xs text-steel">Incasso oggi</div>
            <div className="font-display text-xl font-semibold text-chalk" data-testid="sumup-revenue-today">{summary ? eur(summary.revenue_today) : "—"}</div></div>
        </CardContent></Card>
        <Card className="border-line bg-surface2"><CardContent className="flex items-center gap-3 p-4">
          <Clock className="h-5 w-5 text-steel" />
          <div><div className="text-xs text-steel">Ordini in attesa</div>
            <div className="font-display text-xl font-semibold text-chalk" data-testid="sumup-pending">{summary ? summary.pending : "—"}</div></div>
        </CardContent></Card>
      </div>

      {/* Stato ambiente + verifica connessione */}
      <Card className="border-line bg-surface2">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><CreditCard className="h-4 w-4 text-tertiary" /> Stato ambiente</CardTitle></CardHeader>
        <CardContent className="flex flex-col gap-4">
          {!diag ? <Skeleton className="h-10 w-full" /> : (
            <>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm">
                  {diag.api_key_present ? <CheckCircle2 className="h-4 w-4 text-lime" /> : <XCircle className="h-4 w-4 text-destructive" />}
                  <span className="text-chalk">SUMUP_API_KEY</span>
                  <span className="text-steel">{diag.api_key_present ? "presente" : "mancante"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {diag.merchant_code_present ? <CheckCircle2 className="h-4 w-4 text-lime" /> : <XCircle className="h-4 w-4 text-destructive" />}
                  <span className="text-chalk">SUMUP_MERCHANT_CODE</span>
                  <span className="text-steel">{diag.merchant_code_present ? "presente" : "mancante"}</span>
                </div>
              </div>
              <p className="text-xs text-steel">
                {configured ? "Chiavi presenti sul backend. Usa “Verifica connessione” per confermare che siano valide." : "Imposta le chiavi SumUp nei Secrets (colonna Live) ed effettua un redeploy."}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="secondary" size="sm" onClick={runVerify} disabled={verifying || !configured} data-testid="sumup-verify-button">
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Verifica connessione
                </Button>
                {verifyRes && (
                  <span className={`text-sm ${verifyRes.ok ? "text-lime" : "text-destructive"}`} data-testid="sumup-verify-result">
                    {verifyRes.ok ? `OK · ${verifyRes.merchant_name} (${verifyRes.merchant_code})` : `Errore: ${verifyRes.detail || "verifica non riuscita"}`}
                  </span>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Tabella ordini in tempo reale */}
      <Card className="border-line bg-surface2">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Ordini in tempo reale</CardTitle>
          <div className="flex items-center gap-2 text-xs text-steel">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-lime" /> aggiornamento automatico ogni 10s
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={loadLive} data-testid="sumup-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="sumup-orders-table">
              <TableHeader>
                <TableRow className="border-line hover:bg-transparent">
                  <TableHead>Ordine</TableHead><TableHead>Data</TableHead><TableHead>Importo</TableHead>
                  <TableHead>Stato</TableHead><TableHead>Reference</TableHead><TableHead>ID checkout</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!orders ? (
                  Array.from({ length: 4 }).map((_, i) => <TableRow key={i} className="border-line"><TableCell colSpan={7}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)
                ) : orders.length === 0 ? (
                  <TableRow className="border-line"><TableCell colSpan={7} className="py-10 text-center text-sm text-steel">Nessun checkout ancora avviato.</TableCell></TableRow>
                ) : orders.map((o) => (
                  <TableRow key={o.order_number} className="border-line" data-testid={`sumup-order-${o.order_number}`}>
                    <TableCell className="font-medium text-chalk">{o.order_number}</TableCell>
                    <TableCell className="text-steel">{fmtDate(o.created_at)}</TableCell>
                    <TableCell className="text-chalk">{eur(o.total)}</TableCell>
                    <TableCell><StatusPill s={o.payment_status} /></TableCell>
                    <TableCell className="font-mono text-xs text-steel">{o.checkout_reference || "—"}</TableCell>
                    <TableCell className="max-w-[160px] truncate font-mono text-xs text-steel" title={o.checkout_id}>{o.checkout_id || "—"}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => runRecheck(o.order_number)} disabled={recheckingId === o.order_number} data-testid={`sumup-recheck-${o.order_number}`}>
                        {recheckingId === o.order_number ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Ricontrolla
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
